import { ADMIN_STORAGE_KEY, USER_STORAGE_KEY } from '../api/client';
import type { AdminState } from './admin.slice';
import type { UserState } from './user.slice';

/**
 * Scadenza (epoch in **secondi**) di una sessione nata ora, data la durata in
 * secondi restituita dal server al login (M4). Serve per l'idratazione locale:
 * il token vero è nel cookie `HttpOnly`, non più leggibile da JavaScript.
 */
export function sessionExpiry(ttlSeconds: number): number {
    return Math.floor(Date.now() / 1000) + ttlSeconds;
}

/**
 * Forma minima di una sessione salvata in localStorage: `logged` e una scadenza
 * numerica. M4: **non** contiene più il token (che vive solo nel cookie HttpOnly),
 * quindi un XSS non può rubare credenziali persistenti da qui.
 */
interface StoredSession {
    logged?: boolean;
    exp?: number;
}

/**
 * Legge e valida una sessione da localStorage (A4).
 *
 * Un JSON corrotto, una sessione incompleta o una sessione **già scaduta** (`exp`)
 * vengono rimossi dallo storage, così il prossimo avvio riparte da uno stato pulito
 * invece di restare "loggato" con un cookie che il server rifiuterebbe.
 */
function readSession<T extends StoredSession>(key: string): T | undefined {
    const stored = localStorage.getItem(key);
    if (!stored) return undefined;

    try {
        const parsed = JSON.parse(stored) as T;
        if (parsed?.logged !== true || typeof parsed.exp !== 'number' || !Number.isFinite(parsed.exp)) {
            localStorage.removeItem(key);
            return undefined;
        }

        if (parsed.exp * 1000 <= Date.now()) {
            localStorage.removeItem(key);
            return undefined;
        }

        return parsed;
    } catch {
        localStorage.removeItem(key);
        return undefined;
    }
}

/** Sessione ospite salvata al login (chiave `movimento:user`). */
export function readStoredUser(): UserState | undefined {
    return readSession<UserState>(USER_STORAGE_KEY);
}

/** Sessione admin salvata al login (chiave `movimento:admin`). */
export function readStoredAdmin(): AdminState | undefined {
    return readSession<AdminState>(ADMIN_STORAGE_KEY);
}
